import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { ClientAppName, MembershipSummary } from '../api/types';
import { useApi, useMediaUrl } from '../auth/ApiProvider';
import { entryPath, useEntrySlug } from '../auth/entryPaths';
import { opensElsewhere } from '../auth/memberships';
import { useSession } from '../auth/SessionProvider';
import { passkeysSupported } from '../auth/webauthn';
import { PasskeyFailureNotice } from './Passkeys';
import { normalisePhone } from '../format';
import { useI18n } from '../i18n/I18nProvider';
import { Field, PasswordInput, PhoneInput } from './Field';
import { TenantLogo } from './Photo';
import { Button, Stack } from './primitives';
import { LocaleToggle } from './AppShell';
import { LegalFooter } from './Legal';
import { BrandMark } from './BrandMark';
import { ErrorState } from './states';
import { ThemeToggle } from './ThemeToggle';

export interface BrandPanelProps {
  /** A logo URL — the tenant's own where one is known before login (D-1), else the app's mark. */
  logoUrl?: string | null;
  name: string;
  tagline?: string;
}

export function BrandPanel({ logoUrl, name, tagline }: BrandPanelProps) {
  const resolve = useMediaUrl();
  return (
    <div className="dg-auth__brand">
      {logoUrl
        ? <img className="dg-auth__logo" src={resolve(logoUrl)} alt="" />
        : <span className="dg-topbar__mark" style={{ inlineSize: 56, blockSize: 56 }} aria-hidden="true"><BrandMark size={38} /></span>}
      <p className="dg-auth__name">{name}</p>
      {tagline && <p className="dg-auth__tagline">{tagline}</p>}
    </div>
  );
}

/** The language and theme switches under every screen shown before sign-in. */
export function AuthPreferences() {
  return (
    <>
      <div className="dg-row" style={{ justifyContent: 'center' }}>
        <LocaleToggle />
        <ThemeToggle />
      </div>
      <LegalFooter />
    </>
  );
}

export interface SignInScreenProps extends BrandPanelProps {
  /** Where to go once the session is ready. Defaults to wherever the guard redirected from. */
  redirectTo?: string;
  /** Extra content below the form — a link to the invitation flow, say. */
  footer?: ReactNode;
}

/**
 * Sign-in, shared by all four clients.
 *
 * Phone and password, or a passkey where the browser has them. The API answers every failure identically — unknown number, wrong password,
 * suspended account — so that this form cannot be used to discover which numbers belong to families at a
 * given nursery, and this screen must not undo that by guessing at a more specific message.
 */
export function SignInScreen({ redirectTo, footer, ...brand }: SignInScreenProps) {
  const { t } = useI18n();
  const { signIn, signInWithPasskey } = useSession();
  const { appName } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  // Opened from a nursery's entry link: its links keep the slug, and its one membership there is chosen (Q64).
  const slug = useEntrySlug();
  const prefer = slug ? { tenantSlug: slug, app: appName } : undefined;

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ phone?: string; password?: string }>({});
  const [failure, setFailure] = useState<unknown>(null);
  const [pending, setPending] = useState<'password' | 'passkey' | null>(null);
  const [passkeyFailure, setPasskeyFailure] = useState<unknown>(null);

  const from = (location.state as { from?: string } | null)?.from;
  const destination = redirectTo ?? from ?? '/';

  /**
   * A typed number narrows the device's offer to that account's passkeys; with none, the device offers
   * whatever it holds for this site, which on a personal phone is the smoother path. The API answers a
   * refused passkey exactly as it answers a wrong password, so nothing here tells whose it was.
   */
  async function signInByPasskey() {
    setFailure(null);
    setPasskeyFailure(null);
    setErrors({});
    setPending('passkey');
    try {
      const status = await signInWithPasskey(normalisePhone(phone), prefer);
      navigate(status === 'selecting' ? '/choose' : destination, { replace: true });
    } catch (error) {
      setPasskeyFailure(error);
    } finally {
      setPending(null);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);

    // Checked here so an obviously malformed number does not spend one of the account's attempts before
    // lockout. Anything that looks plausible still goes to the server, which is the only authority.
    const normalised = normalisePhone(phone);
    const next = {
      ...(normalised ? {} : { phone: t('auth.phone.required') }),
      ...(password ? {} : { password: t('auth.password.required') }),
    };
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setPending('password');
    try {
      const status = await signIn(normalised!, password, prefer);
      navigate(status === 'selecting' ? '/choose' : destination, { replace: true });
    } catch (error) {
      setFailure(error);
    } finally {
      setPending(null);
      setPassword('');
    }
  }

  return (
    <div className="dg-auth">
      <div className="dg-auth__panel">
        <BrandPanel {...brand} />

        <form className="dg-card" onSubmit={(event) => void submit(event)} noValidate>
          <Stack>
            <h1 style={{ fontSize: 'var(--dg-text-xl)' }}>{t('auth.signIn')}</h1>

            {failure != null && <ErrorState error={failure} />}

            <Field label={t('auth.phone')} hint={t('auth.phone.hint')} error={errors.phone} required>
              {(props) => (
                <PhoneInput
                  {...props}
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  autoFocus
                />
              )}
            </Field>

            <Field label={t('auth.password')} error={errors.password} required>
              {(props) => (
                <PasswordInput
                  {...props}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                />
              )}
            </Field>

            <Button type="submit" block size="large" busy={pending === 'password'} disabled={pending !== null} busyLabel={t('auth.submitting')}>
              {t('auth.submit')}
            </Button>

            {passkeysSupported() && (
              <>
                <p className="dg-muted dg-text-sm" style={{ textAlign: 'center' }}>{t('auth.passkey.or')}</p>
                {passkeyFailure != null && <PasskeyFailureNotice error={passkeyFailure} during="sign-in" />}
                <Button
                  variant="secondary"
                  block
                  size="large"
                  busy={pending === 'passkey'}
                  disabled={pending !== null}
                  busyLabel={t('auth.submitting')}
                  onClick={() => void signInByPasskey()}
                >
                  {t('auth.passkey.signIn')}
                </Button>
                <p className="dg-muted dg-text-sm" style={{ textAlign: 'center' }}>{t('auth.passkey.signIn.hint')}</p>
              </>
            )}
          </Stack>
        </form>

        <div className="dg-row" style={{ justifyContent: 'space-between' }}>
          <Link to={entryPath('forgot-password', slug)}>{t('auth.forgotPassword')}</Link>
          <Link to={entryPath('invitation', slug)}>{t('auth.haveInvitation')}</Link>
        </div>
        {footer}
        <AuthPreferences />
      </div>
    </div>
  );
}

/**
 * The tenant chooser, shown to the minority who hold more than one membership (D-2, SRS §4.1.2).
 *
 * Each option carries that nursery's own name and logo, so a parent with children at two settings
 * recognises them by sight rather than by reading. Everyone else never sees this screen.
 *
 * Every membership is listed in every app, so nobody thinks one has gone missing; one this app cannot open is
 * disabled and names the app that does (Q53).
 */
export function ChooseMembershipScreen({ redirectTo = '/', ...brand }: BrandPanelProps & { redirectTo?: string }) {
  const { t } = useI18n();
  const { appName } = useApi();
  const { pendingMemberships, chooseMembership, cancelSelection, status } = useSession();
  const navigate = useNavigate();
  const [failure, setFailure] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Landing here without a selection in progress — a reload, or a bookmarked URL — means starting again.
  // The selection token lives only in memory, so there is nothing to resume.
  if (status === 'loading') return null;
  if (status !== 'selecting') {
    return <Navigate to={status === 'authenticated' ? redirectTo : '/sign-in'} replace />;
  }

  async function choose(membership: MembershipSummary) {
    setFailure(null);
    setBusyId(membership.membershipId);
    try {
      await chooseMembership(membership.membershipId);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setFailure(error);
      setBusyId(null);
    }
  }

  return (
    <div className="dg-auth">
      <div className="dg-auth__panel">
        <BrandPanel {...brand} />
        <div className="dg-card">
          <Stack>
            <h1 style={{ fontSize: 'var(--dg-text-xl)' }}>{t('auth.chooseMembership')}</h1>
            <p className="dg-card__hint">{t('auth.chooseMembership.hint')}</p>

            {failure != null && <ErrorState error={failure} />}

            {/* Nothing here can be picked: the API refused the selection token, which ends the choice, or every
                membership opens in another app (Q53). Either way the only way on is to start again. */}
            {pendingMemberships.every((membership) => opensElsewhere(membership, appName) !== undefined) && (
              <Button onClick={cancelSelection}>{t('auth.signInAgain')}</Button>
            )}

            {pendingMemberships.map((membership) => (
              <MembershipOption
                key={membership.membershipId}
                membership={membership}
                busy={busyId === membership.membershipId}
                opensIn={opensElsewhere(membership, appName)}
                onChoose={() => void choose(membership)}
              />
            ))}
          </Stack>
        </div>
        <AuthPreferences />
      </div>
    </div>
  );
}

export function MembershipOption({ membership, busy, opensIn, onChoose }: {
  membership: MembershipSummary;
  busy?: boolean;
  /** The app that opens this membership, where this one cannot: the option is shown, disabled, naming it. */
  opensIn?: ClientAppName;
  onChoose: () => void;
}) {
  const { t, locale } = useI18n();

  const name = membership.scope === 'Platform'
    ? t('auth.membership.platform')
    : (locale === 'en' && membership.tenantNameEn) || membership.tenantNameAr || membership.tenantSlug || '';

  return (
    <button
      type="button"
      className={opensIn ? 'dg-membership-option dg-membership-option--elsewhere' : 'dg-membership-option'}
      onClick={onChoose}
      disabled={busy || opensIn !== undefined}
      data-opens-in={opensIn}
    >
      {membership.scope === 'Platform'
        ? <span className="dg-logo" aria-hidden="true"><BrandMark size={28} /></span>
        : <TenantLogo name={name} logoUrl={membership.tenantLogoUrl} />}
      <span className="dg-stack dg-stack--tight">
        <span className="dg-membership-option__name">{name}</span>
        {membership.roleNameAr && (
          <span className="dg-membership-option__role">{membership.roleNameAr}</span>
        )}
        {opensIn && (
          <span className="dg-membership-option__elsewhere">
            {t(`auth.membership.opensIn.${opensIn}`)}
          </span>
        )}
      </span>
      {busy && <span className="dg-spinner__mark" aria-hidden="true" />}
    </button>
  );
}
